import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import {
  FaTimes,
  FaBars,
  FaChartLine,
  FaShapes,
  FaTshirt,
  FaShoppingCart,
  FaCheckCircle,
  FaMoneyCheckAlt,
  FaFileContract,
  FaLock,
  FaShippingFast,
  FaEnvelope,
  FaPhoneAlt,
  FaCog,
  FaSearch,
  FaStore,
  FaShopify,
  FaTags,
  FaChevronUp, 
  FaChevronDown,
  FaFileInvoice,
  FaMoneyCheck,
  FaWarehouse,
  FaUndoAlt,
  FaTag,
  FaReceipt,
  FaFileInvoiceDollar,
  FaCreditCard
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

const Sidebar = ({ fixedOnDesktop = false }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userShop, setUserShop] = useState(null);
  const [isBrandingOpen, setIsBrandingOpen] = useState(false);
  const [isInvoicesOpen, setIsInvoicesOpen] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [isFounderOpen, setIsFounderOpen] = useState(false);

  const safeSetIsBrandingOpen = setIsBrandingOpen || (() => {});
  const safeSetIsInvoicesOpen = setIsInvoicesOpen || (() => {});
  const safeSetIsPolicyOpen = setIsPolicyOpen || (() => {});
  const safeSetIsFounderOpen = setIsFounderOpen || (() => {});
  
  const user = auth.currentUser;

  useEffect(() => {
    if (user) {
      const fetchUserShop = async () => {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserShop(userDoc.data().shop);
        }
      };
      fetchUserShop();
    }
  }, [user]);

  useEffect(() => {
  const style = document.createElement('style');
  style.innerHTML = `
    ::-webkit-scrollbar {
      width: 6px;
    }
    ::-webkit-scrollbar-track {
      background: transparent;
    }
    ::-webkit-scrollbar-thumb {
      background-color: #4B5563; /* gray-600 */
      border-radius: 10px;
    }
  `;
  document.head.appendChild(style);
    return () => {
    document.head.removeChild(style);
    };
  }, []);


  const location = useLocation();
  const currentPath = (location.pathname || "").toLowerCase();

  const NavLink = ({ to, icon, children }) => {
    const targetPath = (to || "").toLowerCase();
    const isActive = currentPath === targetPath || currentPath.startsWith(targetPath + "/");
    const baseClasses = "flex items-center p-2 rounded-md transition-colors";
    const activeClasses = "bg-gray-100 text-black font-semibold";
    const inactiveClasses = "text-white/80 hover:bg-blue-600 hover:text-white";
    return (
      <Link to={to} className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}>
        <span className="mr-3 w-5 text-center">{icon}</span>
        {children}
      </Link>
    );
  };

  const Dropdown = ({ title, icon, isOpen, toggle, isActive, children }) => (
    <div>
      <div
        onClick={toggle}
        className={`flex justify-between items-center p-2 rounded-md cursor-pointer transition-colors ${
          isActive ? "bg-gray-100 text-black font-semibold" : "text-white/80 hover:bg-blue-600 hover:text-white"
        }`}
      >
        <div className="flex items-center">
          <span className="mr-3 w-5 text-center">{icon}</span> {title}
        </div>
        {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
      </div>
      {isOpen && <div className="pl-10 mt-1 space-y-1">{children}</div>}
    </div>
  );

  

  // Auto-open dropdowns when a child route is active
  useEffect(() => {
    const isBrandingActive = currentPath.startsWith("/necklabels");
    const isInvoicesActive = currentPath.startsWith("/sales-invoice") || currentPath.startsWith("/purchase-invoice");
    const isPolicyActive = [
      "/privacy-policy",
      "/shipping-policy",
      "/terms-and-conditions",
    ].some((p) => currentPath.startsWith(p));
    if (isBrandingActive) setIsBrandingOpen(true);
    if (isInvoicesActive) setIsInvoicesOpen(true);
    if (isPolicyActive) setIsPolicyOpen(true);
  }, [currentPath]);

  if (!user) return null;

  return (
    <>
      {!isSidebarOpen && (
        <div className="md:hidden fixed top-4 left-4 z-50">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 bg-white rounded-md shadow-lg text-gray-800"
          >
            <FaBars size={20} />
          </button>
        </div>
      )}

<aside
  className={`fixed top-0 left-0 h-full bg-white shadow-xl z-40 w-64 transform transition-transform duration-300 ease-in-out
    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
    md:translate-x-0 md:flex-shrink-0`}
>

        <div className="flex flex-col h-screen bg-black sidebar-container" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>


          {/* Fixed Logo Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-black flex-shrink-0">
            <Link to={"/dashboard"} className="flex justify-start w-full">
              <img src="logo.png" alt="Logo" className="w-[70%] h-auto" />
            </Link>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="md:hidden text-white/70 hover:text-white/70 bg-black absolute right-4"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* Scrollable Navigation Content */}
          <nav 
            className="flex-1 bg-black p-2 space-y-1 overflow-y-auto"
            style={{
              scrollbarWidth: 'thin',            // For Firefox
              scrollbarColor: '#4B5563 transparent', // For Firefox
            }}
          >
            <NavLink to="/dashboard" icon={<FaChartLine />}>
              Dashboard
            </NavLink>
            <NavLink
              to={
                user.uid === "hl6gcaUDIFXQkYUxppfAAHycLtO2" ||
                user.uid === "Ge9xCYPWAqcixxj6wzfpHd1XWPF2" ||
                user.uid === "CrQeQiv7iDPcP4HDzpsaeiNscDJ3"
                  ? "/select-tshirt"
                  : "/SelectTshirt"
              }
              icon={<FaTshirt />}
            >
              Create Product
            </NavLink>
            <NavLink
              to={
                user.uid === "hl6gcaUDIFXQkYUxppfAAHycLtO2" ||
                user.uid === "Ge9xCYPWAqcixxj6wzfpHd1XWPF2" ||
                user.uid === "CrQeQiv7iDPcP4HDzpsaeiNscDJ3"
                  ? "/products-new"
                  : "/Products"
              }
              icon={<FaShapes />}
            >
              Products
            </NavLink>
            <NavLink to="/create-order" icon={<FaSearch />}>
              Book a Sample
            </NavLink>

            <Dropdown
              title="Branding"
              icon={<FaTags />}
              isOpen={isBrandingOpen}
              toggle={() => safeSetIsBrandingOpen(!isBrandingOpen)}
              isActive={currentPath.startsWith("/necklabels")}
            >
              <NavLink to="/necklabels" icon={<FaTag />}>Neck Labels</NavLink>
            </Dropdown>

            <NavLink to="/create-order" icon={<FaShoppingCart />}>
              Create Order
            </NavLink>
     {userShop && (
              <NavLink
                to={
                user.uid === "hl6gcaUDIFXQkYUxppfAAHycLtO2" ||
                user.uid === "Ge9xCYPWAqcixxj6wzfpHd1XWPF2" ||
                user.uid === "CrQeQiv7iDPcP4HDzpsaeiNscDJ3"
                    ? "/Orders-new"
                    : "/OrdersPage"
                }
                icon={<FaShopify />}
              >
                Shopify Orders
              </NavLink>
            )}
            <NavLink to="/PaidOrders" icon={<FaCheckCircle />}>
              Confirmed Orders
            </NavLink>

            {user.uid === "6Q1cEoMrjRXbzBMQzfwFeZWHi102" && (
  <NavLink to="/hybrid-inventory" icon={<FaWarehouse />}>
    Unitee Hybrid
  </NavLink>
)}


<Dropdown
  title="Invoices"
  icon={<FaFileInvoice />}
  isOpen={isInvoicesOpen}
 toggle={() => safeSetIsInvoicesOpen(!isInvoicesOpen)}
  isActive={
    [
      "/sales-invoice",
      "/purchase-invoice",
      "/creditnotes",
    ].some((p) => currentPath.startsWith(p))
  }
>
  <NavLink to="/sales-invoice" icon={<FaReceipt />}>Sales Invoice</NavLink>
  <NavLink to="/purchase-invoice" icon={<FaFileInvoiceDollar />}>Purchase Invoice</NavLink>
  <NavLink to="/creditnotes" icon={<FaCreditCard />}>Credit Notes</NavLink>
</Dropdown>


            <NavLink to="/cod-remittance" icon={<FaMoneyCheck />}>
              COD Remittance
            </NavLink>
            <NavLink to="/order-management" icon={<FaShippingFast />}>
              Order Management
            </NavLink>
            <NavLink to="/Transaction" icon={<FaMoneyCheckAlt />}>
              Transactions
            </NavLink>
            <NavLink to="/store-connect" icon={<FaStore />}>
              My Store
            </NavLink>
            <NavLink to="/Setting" icon={<FaCog />}>
              Settings
            </NavLink>
            <NavLink to="/contact-us" icon={<FaEnvelope />}>
              Contact Us
            </NavLink>

            <Dropdown
              title="Call a Founder"
              icon={<FaPhoneAlt />}
              isOpen={isFounderOpen}
             toggle={() => safeSetIsFounderOpen(!isFounderOpen)}
              isActive={false}
            >
              <div className="p-2 text-sm text-gray-600">
                <p>
                  <strong>Rajprit Sahni</strong>
                </p>
                <p>+91 81040 38187</p>
          </div>
            </Dropdown>

           <Dropdown
  title="Our Policy"
  icon={<FaFileContract />}
  isOpen={isPolicyOpen}
 toggle={() => safeSetIsPolicyOpen(!isPolicyOpen)}
  isActive={[
    "/privacy-policy",
    "/cancellation-policy",
    "/refund-policy",
    "/shipping-policy",
    "/terms-and-conditions",
  ].some((p) => currentPath.startsWith(p))}
>
  <NavLink to="/privacy-policy" icon={<FaLock />}>
    Privacy Policy
  </NavLink>

  <NavLink to="/cancellation-policy" icon={<FaFileContract />}>
    Cancellation Policy
  </NavLink>

  <NavLink to="/refund-policy" icon={<FaUndoAlt />}>
    Refund Policy
  </NavLink>

  <NavLink to="/shipping-policy" icon={<FaShippingFast />}>
    Shipping and Delivery
  </NavLink>

  <NavLink to="/terms-and-conditions" icon={<FaFileContract />}>
    Terms and Conditions
  </NavLink>
</Dropdown>

          </nav>
              </div>
      </aside>
    </>
  );
};

export default Sidebar;

